<p-dialog
    header="Add Game"
    [visible]="visibleSignal()"
    (visibleChange)="onVisibleChange($event)"
    [style]="{ width: '60rem' }"
    [breakpoints]="{ '1280px': '95vw' }"
    appendTo="body"
    [modal]="true"
    [closable]="!working()"
    [draggable]="false"
>
    <p-stepper [value]="step()" [linear]="true" class="wizard-stepper">
        <p-step-list>
            <p-step [value]="1">Game template</p-step>
            <p-step [value]="2">Game settings</p-step>
            <p-step [value]="3">Variant template</p-step>
            <p-step [value]="4">Variant settings</p-step>
            <p-step [value]="5">Reviews</p-step>
        </p-step-list>

        <p-step-panels>
            <!-- ============================ Step 1: pick game template ============================ -->
            <p-step-panel [value]="1">
                <ng-template #content>
                    <div class="flex flex-col gap-3 pt-3">
                        @if (templatesService.loading()) {
                        <p-message severity="info" text="Loading templates…" />
                        } @else if (eligibleTemplates().length === 0) {
                        <p-message
                            severity="warn"
                            text="No eligible templates. A template must be active, registered on the game server, and match the website's category (skill-based vs chance-based games)."
                        />
                        } @else {
                        <div class="text-sm text-muted-color">
                            Pick a template to base the new game on. You'll be
                            able to override its values on the next step.
                        </div>
                        <div
                            class="flex flex-col gap-2 max-h-[28rem] overflow-y-auto pr-1"
                        >
                            @for (t of eligibleTemplates(); track
                            trackGameTemplate($index, t)) {
                            <button
                                type="button"
                                class="flex gap-3 p-3 border border-surface-200 dark:border-surface-700 rounded text-left w-full bg-transparent transition-colors cursor-pointer hover:bg-surface-100 dark:hover:bg-surface-800 hover:border-primary-300 dark:hover:border-primary-700"
                                (click)="pickGameTemplate(t)"
                            >
                                @if (t.Template.IconUrl) {
                                <img
                                    [src]="resolveGameContentUrl(t.Template.IconUrl)"
                                    [alt]="t.Template.Title"
                                    class="w-16 h-16 rounded object-cover flex-shrink-0"
                                />
                                } @else {
                                <div
                                    class="w-16 h-16 rounded bg-surface-100 dark:bg-surface-800 flex items-center justify-center flex-shrink-0"
                                >
                                    <i class="pi pi-image text-muted-color"></i>
                                </div>
                                }
                                <div class="flex flex-col gap-1 flex-1 min-w-0">
                                    <div class="font-semibold">
                                        {{ t.Template.Title }}
                                    </div>
                                    @if (t.Template.Description) {
                                    <div
                                        class="text-sm text-muted-color whitespace-pre-line break-words"
                                    >
                                        {{ t.Template.Description }}
                                    </div>
                                    }
                                    <div
                                        class="flex items-center gap-2 mt-1 flex-wrap"
                                    >
                                        <p-tag
                                            [value]="t.Template.GameKind?.Name ?? t.Template.GameKindId"
                                            severity="secondary"
                                        />
                                        <p-tag
                                            [value]="gameModeLabel(t.Template.GameKind?.GameMode)"
                                            severity="info"
                                        />
                                        <p-tag
                                            [value]="winModeLabel(t.Template.GameKind?.WinMode)"
                                            severity="success"
                                        />
                                    </div>
                                </div>
                                <i
                                    class="pi pi-chevron-right text-muted-color flex-shrink-0 mt-1"
                                ></i>
                            </button>
                            }
                        </div>
                        }
                    </div>
                </ng-template>
            </p-step-panel>

            <!-- ============================ Step 2: configure game ============================ -->
            <p-step-panel [value]="2">
                <ng-template #content>
                    @if (selectedGameTemplate(); as wrap) {
                    <div class="flex flex-col gap-3 pt-3">
                        <div
                            class="flex gap-3 items-start p-3 border border-surface-200 dark:border-surface-700 rounded"
                        >
                            @if (wrap.Template.IconUrl) {
                            <img
                                [src]="resolveGameContentUrl(wrap.Template.IconUrl)"
                                [alt]="wrap.Template.Title"
                                class="w-16 h-16 rounded object-cover flex-shrink-0"
                            />
                            } @else {
                            <div
                                class="w-16 h-16 rounded bg-surface-100 dark:bg-surface-800 flex items-center justify-center flex-shrink-0"
                            >
                                <i class="pi pi-image text-muted-color"></i>
                            </div>
                            }
                            <div class="flex flex-col gap-1 flex-1 min-w-0">
                                <div class="font-semibold">
                                    {{ wrap.Template.Title }}
                                </div>
                                @if (wrap.Template.Description) {
                                <div
                                    class="text-sm text-muted-color whitespace-pre-line break-words"
                                >
                                    {{ wrap.Template.Description }}
                                </div>
                                }
                                <div
                                    class="flex items-center gap-2 mt-1 flex-wrap"
                                >
                                    <p-tag
                                        [value]="wrap.Template.GameKind?.Name ?? wrap.Template.GameKindId"
                                        severity="secondary"
                                    />
                                    <p-tag
                                        [value]="gameModeLabel(wrap.Template.GameKind?.GameMode)"
                                        severity="info"
                                    />
                                    <p-tag
                                        [value]="winModeLabel(wrap.Template.GameKind?.WinMode)"
                                        severity="success"
                                    />
                                </div>
                            </div>
                        </div>

                        @if (! createdGame()) {
                        <div class="text-xs text-muted-color">
                            Toggle <span class="font-medium">Override</span> to
                            set a realm-specific value. Otherwise the template
                            value is used.
                        </div>
                        <fieldset class="contents">
                            <!-- IsActive -->
                            <div
                                class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
                            >
                                <div class="flex items-center justify-between">
                                    <label class="font-medium">Active</label>
                                    <div class="flex items-center gap-2">
                                        <p-checkbox
                                            inputId="wiz-ovr-active"
                                            [binary]="true"
                                            [ngModel]="gameDraft().IsActive.override"
                                            (ngModelChange)="toggleGameOverride('IsActive', $event)"
                                            [disabled]="createdGame() !== null"
                                        />
                                        <label
                                            for="wiz-ovr-active"
                                            class="text-sm"
                                            >Override</label
                                        >
                                    </div>
                                </div>
                                <div class="flex items-center gap-3">
                                    <p-checkbox
                                        inputId="wiz-val-active"
                                        [binary]="true"
                                        [ngModel]="gameDraft().IsActive.value"
                                        (ngModelChange)="setGameOverrideValue('IsActive', $event)"
                                        [disabled]="!gameDraft().IsActive.override || createdGame() !== null"
                                    />
                                    <label for="wiz-val-active" class="text-sm"
                                        >{{ gameDraft().IsActive.value ?
                                        'Active' : 'Inactive' }}</label
                                    >
                                    @if (!gameDraft().IsActive.override) {
                                    <span class="text-xs text-muted-color"
                                        >(template: {{
                                        gameTemplatePreview('IsActive')
                                        }})</span
                                    >
                                    }
                                </div>
                            </div>

                            <!-- Title -->
                            <div
                                class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
                            >
                                <div class="flex items-center justify-between">
                                    <label class="font-medium">Title</label>
                                    <div class="flex items-center gap-2">
                                        <p-checkbox
                                            inputId="wiz-ovr-title"
                                            [binary]="true"
                                            [ngModel]="gameDraft().Title.override"
                                            (ngModelChange)="toggleGameOverride('Title', $event)"
                                            [disabled]="createdGame() !== null"
                                        />
                                        <label
                                            for="wiz-ovr-title"
                                            class="text-sm"
                                            >Override</label
                                        >
                                    </div>
                                </div>
                                <input
                                    pInputText
                                    [ngModel]="gameDraft().Title.value"
                                    (ngModelChange)="setGameOverrideValue('Title', $event ?? '')"
                                    [disabled]="!gameDraft().Title.override || createdGame() !== null"
                                    [placeholder]="gameTemplatePreview('Title')"
                                />
                            </div>

                            <!-- Description -->
                            <div
                                class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
                            >
                                <div class="flex items-center justify-between">
                                    <label class="font-medium"
                                        >Description</label
                                    >
                                    <div class="flex items-center gap-2">
                                        <p-checkbox
                                            inputId="wiz-ovr-desc"
                                            [binary]="true"
                                            [ngModel]="gameDraft().Description.override"
                                            (ngModelChange)="toggleGameOverride('Description', $event)"
                                            [disabled]="createdGame() !== null"
                                        />
                                        <label
                                            for="wiz-ovr-desc"
                                            class="text-sm"
                                            >Override</label
                                        >
                                    </div>
                                </div>
                                <textarea
                                    pTextarea
                                    rows="5"
                                    [ngModel]="gameDraft().Description.value"
                                    (ngModelChange)="setGameOverrideValue('Description', $event ?? '')"
                                    [disabled]="!gameDraft().Description.override || createdGame() !== null"
                                    [placeholder]="gameTemplatePreview('Description')"
                                ></textarea>
                            </div>

                            <!-- IsFeatured -->
                            <div
                                class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
                            >
                                <div class="flex items-center justify-between">
                                    <label class="font-medium">Featured</label>
                                    <div class="flex items-center gap-2">
                                        <p-checkbox
                                            inputId="wiz-ovr-featured"
                                            [binary]="true"
                                            [ngModel]="gameDraft().IsFeatured.override"
                                            (ngModelChange)="toggleGameOverride('IsFeatured', $event)"
                                            [disabled]="createdGame() !== null"
                                        />
                                        <label
                                            for="wiz-ovr-featured"
                                            class="text-sm"
                                            >Override</label
                                        >
                                    </div>
                                </div>
                                <div class="flex items-center gap-3">
                                    <p-checkbox
                                        inputId="wiz-val-featured"
                                        [binary]="true"
                                        [ngModel]="gameDraft().IsFeatured.value"
                                        (ngModelChange)="setGameOverrideValue('IsFeatured', $event)"
                                        [disabled]="!gameDraft().IsFeatured.override || createdGame() !== null"
                                    />
                                    <label
                                        for="wiz-val-featured"
                                        class="text-sm"
                                        >{{ gameDraft().IsFeatured.value ? 'Yes'
                                        : 'No' }}</label
                                    >
                                    @if (!gameDraft().IsFeatured.override) {
                                    <span class="text-xs text-muted-color"
                                        >(template: {{
                                        gameTemplatePreview('IsFeatured')
                                        }})</span
                                    >
                                    }
                                </div>
                            </div>

                        </fieldset>
                        } @else {
                        <p-message
                            severity="success"
                            text="Game created. Configure its art below, or continue to variants."
                        />
                        <app-realm-game-art-panel
                            [adminGame]="createdGame()"
                            [siteTemplateId]="siteTemplateId()"
                            [active]="step() === 2 && !! createdGame()"
                            (gameUpdated)="onGameArtUpdated($event)"
                        />
                        }
                    </div>
                    }
                </ng-template>
            </p-step-panel>

            <!-- ============================ Step 3: pick variant template ============================ -->
            <p-step-panel [value]="3">
                <ng-template #content>
                    <div class="flex flex-col gap-3 pt-3">
                        @if (variantsService.templatesLoading()) {
                        <p-message
                            severity="info"
                            text="Loading variant templates…"
                        />
                        } @else if (variantsService.templates().length === 0) {
                        <p-message
                            severity="warn"
                            text="No variant templates available for this game kind on the game server. Register at least one variant template and try again."
                        />
                        <div class="flex justify-start">
                            <p-button
                                label="Retry"
                                icon="pi pi-refresh"
                                severity="secondary"
                                outlined
                                [loading]="variantsService.templatesLoading()"
                                (onClick)="reloadVariantTemplates()"
                            />
                        </div>
                        } @else {
                        <div class="text-sm text-muted-color">
                            Pick a variant template. You'll be able to override
                            its display info and active state on the next step.
                        </div>
                        <div
                            class="flex flex-col gap-2 max-h-[28rem] overflow-y-auto pr-1"
                        >
                            @for (t of variantsService.templates(); track
                            trackVariantTemplate($index, t)) {
                            <button
                                type="button"
                                class="flex flex-col gap-2 p-3 border border-surface-200 dark:border-surface-700 rounded text-left w-full hover:bg-surface-100 dark:hover:bg-surface-800 hover:border-primary-300 dark:hover:border-primary-700 transition-colors cursor-pointer bg-transparent"
                                (click)="pickVariantTemplate(t)"
                            >
                                <div class="flex gap-3 items-start w-full">
                                    @if (t.DisplayInfo.ImageUrl) {
                                    <img
                                        [src]="resolveGameContentUrl(t.DisplayInfo.ImageUrl)"
                                        [alt]="t.DisplayInfo.Name"
                                        class="w-16 h-16 rounded object-cover flex-shrink-0"
                                    />
                                    } @else {
                                    <div
                                        class="w-16 h-16 rounded bg-surface-100 dark:bg-surface-800 flex items-center justify-center flex-shrink-0"
                                    >
                                        <i
                                            class="pi pi-image text-muted-color"
                                        ></i>
                                    </div>
                                    }
                                    <div
                                        class="flex flex-col gap-2 flex-1 min-w-0"
                                    >
                                        <div
                                            class="flex items-center gap-2 flex-wrap"
                                        >
                                            <div class="font-semibold">
                                                {{ t.DisplayInfo.Name ||
                                                t.GameVariantTemplateId }}
                                            </div>
                                            @if (!t.IsActive) {
                                            <p-tag
                                                value="Template inactive"
                                                severity="warn"
                                            />
                                            }
                                        </div>
                                        @if (t.DisplayInfo.Description) {
                                        <div
                                            class="text-sm text-muted-color line-clamp-2"
                                        >
                                            {{ t.DisplayInfo.Description }}
                                        </div>
                                        }
                                        <div
                                            class="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-xs mt-1"
                                        >
                                            <div class="flex flex-col gap-0.5">
                                                <span
                                                    class="text-muted-color uppercase tracking-wide"
                                                    >Type</span
                                                >
                                                <span class="font-medium"
                                                    >{{
                                                    modeConfigTypeLabel(t.ModeConfig)
                                                    }}</span
                                                >
                                            </div>
                                            <div class="flex flex-col gap-0.5">
                                                <span
                                                    class="text-muted-color uppercase tracking-wide"
                                                    >Players</span
                                                >
                                                <span
                                                    class="font-medium inline-flex items-center gap-1"
                                                >
                                                    <i
                                                        class="pi pi-users text-xs"
                                                    ></i>
                                                    {{
                                                    variantPlayerCount(t.ModeConfig)
                                                    ?? '—' }}
                                                </span>
                                            </div>
                                            <div class="flex flex-col gap-0.5">
                                                <span
                                                    class="text-muted-color uppercase tracking-wide"
                                                    >Entry Fee</span
                                                >
                                                <span class="font-medium">
                                                    @switch
                                                    (variantEntryFee(t.ModeConfig).kind)
                                                    { @case ('fixed') {
                                                    <app-funds-display
                                                        [funds]="$any(variantEntryFee(t.ModeConfig)).funds"
                                                    />
                                                    } @case ('range') { {{
                                                    $any(variantEntryFee(t.ModeConfig)).min
                                                    }}–{{
                                                    $any(variantEntryFee(t.ModeConfig)).max
                                                    }}
                                                    <span
                                                        class="text-muted-color"
                                                        >({{
                                                        $any(variantEntryFee(t.ModeConfig)).currency
                                                        }})</span
                                                    >
                                                    } @default { — } }
                                                </span>
                                            </div>
                                            <div class="flex flex-col gap-0.5">
                                                <span
                                                    class="text-muted-color uppercase tracking-wide"
                                                    >Reward (1st)</span
                                                >
                                                <span
                                                    class="font-medium inline-flex items-center gap-1"
                                                >
                                                    @if
                                                    (variantTopReward(t.ModeConfig);
                                                    as r) {
                                                    <i
                                                        class="pi pi-trophy text-xs"
                                                    ></i>
                                                    <app-funds-display
                                                        [funds]="r"
                                                    />
                                                    } @else { — }
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <i
                                        class="pi pi-chevron-right text-muted-color flex-shrink-0 mt-1"
                                    ></i>
                                </div>
                                @if (t.Notes) {
                                <div
                                    class="text-xs text-muted-color border-t border-surface-200 dark:border-surface-700 pt-2 mt-1 w-full"
                                >
                                    <span class="font-medium">Notes:</span> {{
                                    t.Notes }}
                                </div>
                                }
                            </button>
                            }
                        </div>
                        }
                    </div>
                </ng-template>
            </p-step-panel>

            <!-- ============================ Step 4: configure variant ============================ -->
            <p-step-panel [value]="4">
                <ng-template #content>
                    @if (selectedVariantTemplate(); as t) {
                    <div
                        class="flex flex-col gap-4 pt-3"
                        (keydown.control.enter)="createVariantAndFinish()"
                        (keydown.meta.enter)="createVariantAndFinish()"
                    >
                        <div
                            class="flex flex-col gap-2 p-3 border border-surface-200 dark:border-surface-700 rounded"
                        >
                            <div class="flex gap-3 items-start">
                                @if (t.DisplayInfo.ImageUrl) {
                                <img
                                    [src]="resolveGameContentUrl(t.DisplayInfo.ImageUrl)"
                                    [alt]="t.DisplayInfo.Name"
                                    class="w-16 h-16 rounded object-cover flex-shrink-0"
                                />
                                } @else {
                                <div
                                    class="w-16 h-16 rounded bg-surface-100 dark:bg-surface-800 flex items-center justify-center flex-shrink-0"
                                >
                                    <i class="pi pi-image text-muted-color"></i>
                                </div>
                                }
                                <div class="flex flex-col gap-2 flex-1 min-w-0">
                                    <div
                                        class="flex items-center gap-2 flex-wrap"
                                    >
                                        <div class="font-semibold">
                                            {{ variantPreviewName(t) }}
                                        </div>
                                        @if (variantDraft().overrideDisplayInfo)
                                        {
                                        <p-tag
                                            value="Overridden"
                                            severity="info"
                                        />
                                        } @if (!t.IsActive) {
                                        <p-tag
                                            value="Template inactive"
                                            severity="warn"
                                        />
                                        }
                                    </div>
                                    @if (variantPreviewDescription(t); as desc)
                                    {
                                    <div
                                        class="text-sm text-muted-color whitespace-pre-line break-words"
                                    >
                                        {{ desc }}
                                    </div>
                                    }
                                    <div
                                        class="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-xs mt-1"
                                    >
                                        <div class="flex flex-col gap-0.5">
                                            <span
                                                class="text-muted-color uppercase tracking-wide"
                                                >Type</span
                                            >
                                            <span class="font-medium"
                                                >{{
                                                modeConfigTypeLabel(t.ModeConfig)
                                                }}</span
                                            >
                                        </div>
                                        <div class="flex flex-col gap-0.5">
                                            <span
                                                class="text-muted-color uppercase tracking-wide"
                                                >Players</span
                                            >
                                            <span
                                                class="font-medium inline-flex items-center gap-1"
                                            >
                                                <i
                                                    class="pi pi-users text-xs"
                                                ></i>
                                                {{
                                                variantPlayerCount(t.ModeConfig)
                                                ?? '—' }}
                                            </span>
                                        </div>
                                        <div class="flex flex-col gap-0.5">
                                            <span
                                                class="text-muted-color uppercase tracking-wide"
                                                >Entry Fee</span
                                            >
                                            <span class="font-medium">
                                                @switch
                                                (variantEntryFee(t.ModeConfig).kind)
                                                { @case ('fixed') {
                                                <app-funds-display
                                                    [funds]="$any(variantEntryFee(t.ModeConfig)).funds"
                                                />
                                                } @case ('range') { {{
                                                $any(variantEntryFee(t.ModeConfig)).min
                                                }}–{{
                                                $any(variantEntryFee(t.ModeConfig)).max
                                                }}
                                                <span class="text-muted-color"
                                                    >({{
                                                    $any(variantEntryFee(t.ModeConfig)).currency
                                                    }})</span
                                                >
                                                } @default { — } }
                                            </span>
                                        </div>
                                        <div class="flex flex-col gap-0.5">
                                            <span
                                                class="text-muted-color uppercase tracking-wide"
                                                >Reward (1st)</span
                                            >
                                            <span
                                                class="font-medium inline-flex items-center gap-1"
                                            >
                                                @if
                                                (variantTopReward(t.ModeConfig);
                                                as r) {
                                                <i
                                                    class="pi pi-trophy text-xs"
                                                ></i>
                                                <app-funds-display
                                                    [funds]="r"
                                                />
                                                } @else { — }
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            @if (t.Notes) {
                            <div
                                class="text-xs text-muted-color border-t border-surface-200 dark:border-surface-700 pt-2 mt-1"
                            >
                                <span class="font-medium">Notes:</span> {{
                                t.Notes }}
                            </div>
                            }
                        </div>

                        <div
                            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
                        >
                            <div class="flex items-center gap-2">
                                <p-checkbox
                                    inputId="wiz-var-ovr-display"
                                    [binary]="true"
                                    [ngModel]="variantDraft().overrideDisplayInfo"
                                    (ngModelChange)="setVariantField('overrideDisplayInfo', $event)"
                                />
                                <label
                                    for="wiz-var-ovr-display"
                                    class="font-medium"
                                    >Override display info</label
                                >
                            </div>
                            @if (variantDraft().overrideDisplayInfo) {
                            <div class="flex flex-col gap-3 pl-6">
                                <div class="flex flex-col gap-1">
                                    <label class="text-sm font-medium"
                                        >Name
                                        <span class="text-red-500"
                                            >*</span
                                        ></label
                                    >
                                    <input
                                        pInputText
                                        [ngModel]="variantDraft().displayName"
                                        (ngModelChange)="setVariantField('displayName', $event ?? '')"
                                    />
                                </div>
                                <div class="flex flex-col gap-1">
                                    <label class="text-sm font-medium"
                                        >Description</label
                                    >
                                    <textarea
                                        pTextarea
                                        rows="2"
                                        [ngModel]="variantDraft().displayDescription"
                                        (ngModelChange)="setVariantField('displayDescription', $event ?? '')"
                                    ></textarea>
                                </div>
                            </div>
                            }
                        </div>

                        <div
                            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
                        >
                            <div class="flex items-center gap-2">
                                <p-checkbox
                                    inputId="wiz-var-ovr-active"
                                    [binary]="true"
                                    [ngModel]="variantDraft().overrideIsActive"
                                    (ngModelChange)="setVariantField('overrideIsActive', $event)"
                                />
                                <label
                                    for="wiz-var-ovr-active"
                                    class="font-medium"
                                    >Override active</label
                                >
                            </div>
                            @if (variantDraft().overrideIsActive) {
                            <div class="flex items-center gap-2 pl-6">
                                <p-checkbox
                                    inputId="wiz-var-active-val"
                                    [binary]="true"
                                    [ngModel]="variantDraft().isActive"
                                    (ngModelChange)="setVariantField('isActive', $event)"
                                />
                                <label for="wiz-var-active-val" class="text-sm"
                                    >{{ variantDraft().isActive ? 'Active' :
                                    'Inactive' }}</label
                                >
                            </div>
                            } @else {
                            <div class="text-xs text-muted-color pl-6">
                                Inherits from template: {{ t.IsActive ? 'Active'
                                : 'Inactive' }}
                            </div>
                            }
                        </div>
                    </div>
                    }
                </ng-template>
            </p-step-panel>

            <!-- ============================ Step 5: seed fake reviews (optional) ============================ -->
            <p-step-panel [value]="5">
                <ng-template #content>
                    <div class="flex flex-col gap-3 pt-3">
                        <p-message
                            severity="success"
                            text="Game and variant created. Optionally seed the game with fake reviews now, or skip and do it later from the Reviews dialog."
                        />

                        @if (seedTags().length === 0) {
                        <p-message
                            severity="warn"
                            text="No tags resolved for this game, so reviews can't be generated. Skip this step."
                        />
                        } @else {
                        <app-review-distribution-form
                            [(targetAverage)]="seedTargetAverage"
                            [(count)]="seedCount"
                            [(concentration)]="seedConcentration"
                            [(createdFrom)]="seedFrom"
                            [(createdTo)]="seedTo"
                            [tags]="seedTags()"
                            idPrefix="wiz-seed"
                        />
                        } @if (seedError()) {
                        <p-message severity="warn" [text]="seedError()!" />
                        }
                    </div>
                </ng-template>
            </p-step-panel>
        </p-step-panels>
    </p-stepper>

    @if (stepError()) {
    <div class="mt-3">
        <p-message severity="error" [text]="stepError()!" />
    </div>
    }

    <ng-template #footer>
        <div class="flex justify-between items-center gap-2 w-full">
            <div class="text-xs text-muted-color">
                @if (createdGame() && step() < 5) { Game has been created.
                Cancelling will leave it without a variant — you can add one
                later from the Variants dialog. } @else if (step() === 5) { Game
                is ready. Skipping only omits the seeded reviews. }
            </div>
            <div class="flex gap-2">
                @if (step() !== 5) {
                <p-button
                    label="Cancel"
                    severity="secondary"
                    outlined
                    [disabled]="working()"
                    (onClick)="close()"
                />
                } @switch (step()) { @case (1) {
                <p-button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    [disabled]="!selectedGameTemplate()"
                    (onClick)="step.set(2)"
                />
                } @case (2) { @if (! createdGame()) {
                <p-button
                    label="Back"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    [disabled]="working()"
                    (onClick)="backToStep1()"
                />
                <p-button
                    label="Create & continue"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    [loading]="working()"
                    [disabled]="!canSaveGameStep()"
                    (onClick)="createGameAndContinue()"
                />
                } @else {
                <p-button
                    label="Continue"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    (onClick)="step.set(3)"
                />
                } } @case (3) {
                <p-button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    [disabled]="!selectedVariantTemplate()"
                    (onClick)="step.set(4)"
                />
                } @case (4) {
                <p-button
                    label="Back"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    [disabled]="working()"
                    (onClick)="backToStep3()"
                />
                <p-button
                    label="Create variant"
                    icon="pi pi-check"
                    [loading]="working()"
                    [disabled]="!canSaveVariantStep()"
                    (onClick)="createVariantAndFinish()"
                />
                } @case (5) {
                <p-button
                    label="Skip & finish"
                    severity="secondary"
                    [disabled]="working()"
                    (onClick)="skipSeed()"
                />
                <p-button
                    label="Generate & finish"
                    icon="pi pi-check"
                    [loading]="working()"
                    [disabled]="seedDisabled()"
                    (onClick)="seedReviews()"
                />
                } }
            </div>
        </div>
    </ng-template>
</p-dialog>
